<script>
/**
 * @component PrescriptionPurchase
 * @description Wizard de compra de medicamento desde una receta. 4 pasos:
 *   1. Receta (resumen) → 2. Elegir farmacia → 3. Elegir medicamento → 4. Confirmar pedido.
 * @example <PrescriptionPurchase />
 */
import indicationService from "@/services/indication";
import pharmacyService from "@/services/pharmacy";
import medicationCatalog from "@/services/medication-catalog";
import Spinner from "@/components/ui/Spinner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon paths (bug conocido con webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const STEPS = [
  { number: 1, label: "Receta" },
  { number: 2, label: "Farmacia" },
  { number: 3, label: "Medicamento" },
  { number: 4, label: "Confirmar" },
];

export default {
  name: "PrescriptionPurchase",

  components: { Spinner },

  data() {
    return {
      steps: STEPS,
      currentStep: 1,
      isLoading: true,
      loadError: false,
      // Step 1
      indication: null,
      // Step 2
      pharmacies: [],
      isLoadingPharmacies: false,
      selectedPharmacy: null,
      // Step 3
      medications: [],
      isLoadingMeds: false,
      selectedMedication: null,
      // Step 4
      deliveryType: "pickup",
      isSubmitting: false,
      // Map
      map: null,
      markers: {},
      defaultIcon: null,
      selectedIcon: null,
    };
  },

  computed: {
    /** @returns {string} Nombre del medicamento recetado */
    drugName() {
      if (!this.indication) return "";
      return (
        this.indication.publicMedication?.name ||
        this.indication.drugName?.name ||
        this.indication.title ||
        "Medicamento"
      );
    },

    /** @returns {number} Cantidad recetada */
    quantity() {
      return this.indication?.quantity || 1;
    },

    /** @returns {string} Posología */
    dosage() {
      return this.indication?.dosage || "-";
    },

    /** @returns {string} Nombre del médico */
    doctorName() {
      const d = this.indication?.doctor;
      if (!d) return "-";
      return `Dr/a. ${d.lastName || ""}, ${d.firstName || ""}`.trim();
    },

    /** @returns {string} Fecha de la receta formateada */
    prescriptionDate() {
      if (!this.indication?.createdAt) return "-";
      return new Date(this.indication.createdAt).toLocaleDateString("es-AR");
    },

    /** @returns {boolean} Si la farmacia seleccionada hace delivery */
    pharmacyHasDelivery() {
      return !!this.selectedPharmacy?.hasDelivery;
    },

    /** @returns {number} Subtotal (precio x cantidad) */
    subtotal() {
      if (!this.selectedMedication) return 0;
      return this.selectedMedication.price * this.quantity;
    },

    /** @returns {number} Monto de cobertura */
    coverageAmount() {
      if (!this.selectedMedication) return 0;
      return Math.round(
        this.subtotal * (this.selectedMedication.coveragePercent / 100)
      );
    },

    /** @returns {number} Total copago */
    totalCopay() {
      return this.subtotal - this.coverageAmount;
    },
  },

  async created() {
    await this.loadIndication();
  },

  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  methods: {
    /**
     * @description Inicializa el mapa Leaflet con los markers de farmacias
     */
    initMap() {
      this.$nextTick(() => {
        const el = this.$refs.pharmacyMap;
        if (!el || this.map) return;

        this.defaultIcon = L.divIcon({
          className: "pharmacy-marker",
          html: '<div class="pharmacy-marker__pin"></div>',
          iconSize: [24, 24],
          iconAnchor: [12, 24],
          popupAnchor: [0, -24],
        });

        this.selectedIcon = L.divIcon({
          className: "pharmacy-marker pharmacy-marker--selected",
          html: '<div class="pharmacy-marker__pin pharmacy-marker__pin--selected"></div>',
          iconSize: [30, 30],
          iconAnchor: [15, 30],
          popupAnchor: [0, -30],
        });

        this.map = L.map(el, { zoomControl: true }).setView(
          [-34.6037, -58.3816],
          12
        );

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap contributors",
          maxZoom: 18,
        }).addTo(this.map);

        this.addPharmacyMarkers();

        // invalidateSize para que Leaflet recalcule tiles correctamente
        setTimeout(() => {
          if (this.map) this.map.invalidateSize();
        }, 200);
      });
    },

    /**
     * @description Agrega markers al mapa por cada farmacia con coordenadas
     */
    addPharmacyMarkers() {
      if (!this.map) return;
      const bounds = [];

      this.pharmacies.forEach((p) => {
        const coords = p.location?.coordinates;
        if (!Array.isArray(coords) || coords.length < 2) return;
        const lat = coords[1];
        const lng = coords[0];
        if (!lat || !lng) return;

        const marker = L.marker([lat, lng], { icon: this.defaultIcon }).addTo(
          this.map
        );
        const name = p.commercialName || p.businessName || "Farmacia";
        const addr = p.address || "";
        marker.bindPopup(
          `<strong>${name}</strong>${addr ? "<br>" + addr : ""}`
        );
        marker.on("click", () => this.selectPharmacy(p));
        this.markers[p._id] = marker;
        bounds.push([lat, lng]);
      });

      if (bounds.length > 0) {
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
      }
    },

    /**
     * @description Centra el mapa en la farmacia seleccionada y resalta su marker
     * @param {Object|null} pharmacy - Farmacia seleccionada
     */
    highlightMarker(pharmacy) {
      if (!this.map) return;

      Object.entries(this.markers).forEach(([id, marker]) => {
        marker.setIcon(
          pharmacy && id === pharmacy._id ? this.selectedIcon : this.defaultIcon
        );
      });

      if (pharmacy) {
        const marker = this.markers[pharmacy._id];
        if (marker) {
          this.map.setView(marker.getLatLng(), 15, { animate: true });
          marker.openPopup();
        }
      }
    },

    /**
     * @description Carga la indicación/receta desde el backend.
     *   El mapa se inicializa DESPUÉS de que isLoading=false para que
     *   el contenedor .pharmacy-map exista en el DOM.
     */
    async loadIndication() {
      this.isLoading = true;
      this.loadError = false;
      try {
        const id = this.$route.params.indicationId;
        this.indication = await indicationService.getById(id);
        this.currentStep = 2;
        await this.loadPharmacies();
      } catch (error) {
        console.error("Error al cargar receta:", error);
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }
      // Inicializar mapa después de que isLoading=false sea aplicado al DOM.
      // No llamar desde loadPharmacies() porque en ese momento isLoading
      // sigue siendo true y el contenedor ref="pharmacyMap" no existe.
      if (!this.loadError) {
        this.$nextTick(() => this.initMap());
      }
    },

    /**
     * @description Carga las farmacias disponibles
     */
    async loadPharmacies() {
      this.isLoadingPharmacies = true;
      try {
        const result = await pharmacyService.query({
          status: "active",
          limit: 50,
        });
        this.pharmacies = result.docs || [];
      } catch (error) {
        console.error("Error al cargar farmacias:", error);
        this.$message.error("Error al cargar farmacias");
      } finally {
        this.isLoadingPharmacies = false;
      }
    },

    /**
     * @description Carga alternativas de medicamento (mock)
     */
    async loadMedications() {
      this.isLoadingMeds = true;
      try {
        this.medications = await medicationCatalog.getAlternatives(
          this.drugName
        );
      } catch (error) {
        console.error("Error al cargar medicamentos:", error);
        this.$message.error("Error al cargar medicamentos");
      } finally {
        this.isLoadingMeds = false;
      }
    },

    /**
     * @description Selecciona una farmacia y avanza al paso 3
     * @param {Object} pharmacy - Farmacia seleccionada
     */
    selectPharmacy(pharmacy) {
      this.selectedPharmacy = pharmacy;
      this.highlightMarker(pharmacy);
    },

    /**
     * @description Selecciona un medicamento
     * @param {Object} med - Medicamento seleccionado
     */
    selectMedication(med) {
      if (!med.inStock) return;
      this.selectedMedication = med;
    },

    /**
     * @description Avanza al siguiente paso
     */
    async nextStep() {
      if (this.currentStep === 2 && this.selectedPharmacy) {
        this.currentStep = 3;
        await this.loadMedications();
      } else if (this.currentStep === 3 && this.selectedMedication) {
        this.currentStep = 4;
        if (this.pharmacyHasDelivery) {
          this.deliveryType = "delivery";
        } else {
          this.deliveryType = "pickup";
        }
      }
    },

    /**
     * @description Retrocede al paso anterior
     */
    prevStep() {
      if (this.currentStep > 2) {
        if (this.currentStep === 4) {
          this.selectedMedication = null;
        }
        this.currentStep--;
      } else {
        this.goBack();
      }
    },

    /**
     * @description Confirma el pedido (mock) y vuelve a la historia clínica
     */
    async confirmOrder() {
      this.isSubmitting = true;
      try {
        // TODO: POST real al backend cuando esté implementado
        await new Promise((resolve) => setTimeout(resolve, 800));
        this.$message.success("Pedido enviado");
        this.goBack();
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * @description Vuelve a la historia clínica del paciente
     */
    goBack() {
      if (this.$route.params.id) {
        this.$router.push({
          name: "patient-dashboard",
          params: { id: this.$route.params.id },
        });
      } else {
        this.$router.push({ name: "patient-me" });
      }
    },

    /**
     * @description Formatea un número como moneda AR
     * @param {number} value - Valor
     * @returns {string}
     */
    formatCurrency(value) {
      return `$${(value || 0).toLocaleString("es-AR")}`;
    },

    /**
     * @description Devuelve el label del tipo de medicamento
     * @param {string} type - Tipo
     * @returns {string}
     */
    getMedTypeLabel(type) {
      const map = {
        original: "Recetado",
        generic: "Genérico",
        similar: "Similar",
      };
      return map[type] || type;
    },

    /**
     * @description Devuelve la clase CSS del badge del tipo de medicamento
     * @param {string} type - Tipo
     * @returns {string}
     */
    getMedTypeBadge(type) {
      const map = {
        original: "med-badge--original",
        generic: "med-badge--generic",
        similar: "med-badge--similar",
      };
      return map[type] || "";
    },
  },
};
</script>

<template lang="pug">
.purchase-page
  .purchase-card
    //- Header
    .purchase-header
      button.purchase-header__back(@click="goBack")
        micon(name="arrow_back")
      h2.purchase-header__title Comprar medicamento

    //- Stepper
    .steps
      template(v-for="(step, idx) in steps")
        .step(
          :key="step.number"
          :class="{ 'step--done': currentStep > step.number, 'step--active': currentStep === step.number }"
        )
          .step__number
            span(v-if="currentStep > step.number") ✓
            span(v-else) {{ step.number }}
          span.step__label {{ step.label }}
        .step__line(
          v-if="idx < steps.length - 1"
          :key="'line-' + step.number"
          :class="{ 'step__line--done': currentStep > step.number }"
        )

    //- Loading
    .loading-container(v-if="isLoading")
      spinner

    //- Error state
    .error-container(v-else-if="loadError")
      p.error-container__text No se pudo cargar la receta. Verificá tu conexión e intentá nuevamente.
      .error-container__actions
        el-button.btn-ghost(@click="goBack") Volver
        el-button.btn-primary(type="primary" @click="loadIndication") Reintentar

    template(v-else)
      //- Step 1 & 2 — Prescription summary (always visible once loaded)
      .prescription-summary(v-if="indication")
        .prescription-summary__left
          .prescription-summary__icon
            micon(name="medication")
          .prescription-summary__info
            .prescription-summary__drug {{ drugName }}
            .prescription-summary__meta {{ dosage }} · Cantidad: {{ quantity }}
        .prescription-summary__right
          .prescription-summary__doctor {{ doctorName }}
          .prescription-summary__date {{ prescriptionDate }}
          span.badge-vigente Vigente

      //- Step 2 — Elegir farmacia
      template(v-if="currentStep === 2")
        h3.section-title Elegir farmacia

        .loading-container(v-if="isLoadingPharmacies")
          spinner

        .pharmacy-split(v-else)
          .pharmacy-split__list
            .pharmacy-list-scroll
              .pharmacy-row(
                v-for="p in pharmacies"
                :key="p._id"
                :class="{ 'pharmacy-row--selected': selectedPharmacy && selectedPharmacy._id === p._id }"
                @click="selectPharmacy(p)"
              )
                .pharmacy-row__icon
                  micon(name="local_pharmacy")
                .pharmacy-row__info
                  .pharmacy-row__name {{ p.commercialName || p.businessName || '-' }}
                  .pharmacy-row__address {{ p.address || '-' }}, {{ p.city || '' }}
                  .pharmacy-row__tags
                    span.pharmacy-tag.pharmacy-tag--delivery(v-if="p.hasDelivery") Delivery
                    span.pharmacy-tag.pharmacy-tag--online(v-if="p.sellsOnline") Venta online
                .pharmacy-row__distance — km
                .pharmacy-row__radio
                  .pharmacy-radio(:class="{ 'pharmacy-radio--checked': selectedPharmacy && selectedPharmacy._id === p._id }")
                    span.pharmacy-radio__dot(v-if="selectedPharmacy && selectedPharmacy._id === p._id")

              .empty-state(v-if="!pharmacies.length")
                p No hay farmacias disponibles

          .pharmacy-split__map
            .pharmacy-map(ref="pharmacyMap")

        .step-actions
          el-button.btn-ghost(@click="goBack") Cancelar
          el-button.btn-primary(type="primary" @click="nextStep" :disabled="!selectedPharmacy") Continuar

      //- Step 3 — Elegir medicamento
      template(v-if="currentStep === 3")
        h3.section-title
          | Elegir medicamento
          span.mock-badge Datos mock — servicio externo

        .loading-container(v-if="isLoadingMeds")
          spinner

        .medication-list(v-else)
          .medication-card(
            v-for="med in medications"
            :key="med.id"
            :class="{ 'medication-card--selected': selectedMedication && selectedMedication.id === med.id, 'medication-card--original': med.type === 'original', 'medication-card--disabled': !med.inStock }"
            @click="selectMedication(med)"
          )
            .medication-card__header
              .medication-card__name {{ med.name }}
              span.med-badge(:class="getMedTypeBadge(med.type)") {{ getMedTypeLabel(med.type) }}
            .medication-card__presentation {{ med.presentation }} · {{ med.lab }}
            .medication-card__pricing
              .medication-card__price {{ formatCurrency(med.price) }}
              span.medication-card__coverage Cobertura {{ med.coveragePercent }}%
              span.medication-card__copay Copago: {{ formatCurrency(med.copay) }}
            .medication-card__stock
              span.stock-badge(:class="med.inStock ? 'stock-badge--ok' : 'stock-badge--no'")
                | {{ med.inStock ? 'En stock' : 'Sin stock' }}
            .medication-card__check(v-if="med.inStock")
              span(v-if="selectedMedication && selectedMedication.id === med.id") ✓

        .step-actions
          el-button.btn-ghost(@click="prevStep") Volver
          el-button.btn-primary(type="primary" @click="nextStep" :disabled="!selectedMedication") Continuar

      //- Step 4 — Confirmar
      template(v-if="currentStep === 4")
        h3.section-title Confirmar pedido

        .confirm-section
          .confirm-block
            .confirm-block__label Medicamento
            .confirm-block__value {{ selectedMedication.name }} — {{ selectedMedication.presentation }}
          .confirm-block
            .confirm-block__label Cantidad
            .confirm-block__value {{ quantity }}
          .confirm-block
            .confirm-block__label Farmacia
            .confirm-block__value {{ selectedPharmacy.commercialName || selectedPharmacy.businessName }}
          .confirm-block
            .confirm-block__label Tipo de entrega
            .confirm-delivery
              el-radio-group(v-model="deliveryType")
                el-radio(label="pickup") Retiro en local
                el-radio(label="delivery" :disabled="!pharmacyHasDelivery") Delivery

          .confirm-divider

          .confirm-block
            .confirm-block__label Precio unitario
            .confirm-block__value {{ formatCurrency(selectedMedication.price) }}
          .confirm-block
            .confirm-block__label Subtotal (x{{ quantity }})
            .confirm-block__value {{ formatCurrency(subtotal) }}
          .confirm-block
            .confirm-block__label Cobertura obra social ({{ selectedMedication.coveragePercent }}%)
            .confirm-block__value.confirm-block__value--discount -{{ formatCurrency(coverageAmount) }}
          .confirm-block.confirm-block--total
            .confirm-block__label Total copago
            .confirm-block__value.confirm-block__value--total {{ formatCurrency(totalCopay) }}

        .step-actions
          el-button.btn-ghost(@click="prevStep") Volver
          el-button.btn-primary(type="primary" @click="confirmOrder" :loading="isSubmitting") Confirmar pedido
</template>

<style lang="scss" scoped>
.purchase-page {
  padding: 0;
}

.purchase-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 32px;
  margin-top: 16px;
}

.purchase-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  &__back {
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background 0.15s;

    .micon {
      width: 18px;
      height: 18px;
      fill: #6b7294;
    }

    &:hover {
      background: #f1f5f9;
    }
  }

  &__title {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #002d5e;
    margin: 0;
  }
}

// ─── Stepper ───
.steps {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
}
.step {
  display: flex;
  align-items: center;
  gap: 8px;
  &__number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #6b7294;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
  }
  &--active &__number {
    background: #002d5e;
    color: #fff;
  }
  &--active &__label {
    color: #002d5e;
  }
  &--done &__number {
    background: #05e5ba;
    color: #002d5e;
  }
  &--done &__label {
    color: #002d5e;
  }
  &__line {
    flex: 1;
    height: 2px;
    background: #e2e8f0;
    margin: 0 12px;
    &--done {
      background: #05e5ba;
    }
  }
}

// ─── Prescription summary ───
.prescription-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 24px;
  gap: 16px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #002d5e;
    display: flex;
    align-items: center;
    justify-content: center;

    .micon {
      fill: #fff;
      width: 20px;
      height: 20px;
    }
  }

  &__drug {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #002d5e;
  }

  &__meta {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
    margin-top: 2px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  &__doctor,
  &__date {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
  }
}

.badge-vigente {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #16a34a;
  background: #dcfce7;
  padding: 3px 10px;
  border-radius: 20px;
}

.section-title {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mock-badge {
  font-size: 10px;
  font-weight: 600;
  color: #f59e0b;
  background: #fef3c7;
  padding: 3px 8px;
  border-radius: 4px;
}

.loading-container {
  padding: 40px 0;
  text-align: center;
}

.error-container {
  padding: 48px 0;
  text-align: center;

  &__text {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    color: #6b7294;
    margin-bottom: 20px;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
}

// ─── Pharmacy split layout ───
.pharmacy-split {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  min-height: 400px;

  &__list {
    flex: 0 0 60%;
    min-width: 0;
  }

  &__map {
    flex: 0 0 calc(40% - 16px);
    min-width: 0;
  }
}

.pharmacy-list-scroll {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.pharmacy-map {
  width: 100%;
  height: 400px;
  min-height: 400px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  z-index: 1;
}

.pharmacy-row {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 18px;
  cursor: pointer;
  transition: all 0.15s;
  width: 100%;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &--selected {
    border-color: #05e5ba;
    background: #e8fdf8;
  }

  &__icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 10px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;

    .micon {
      fill: #002d5e;
      width: 20px;
      height: 20px;
    }
  }

  &--selected &__icon {
    background: #d1faf0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #002d5e;
    margin-bottom: 2px;
  }

  &__address {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
    margin-bottom: 6px;
  }

  &__tags {
    display: flex;
    gap: 6px;
  }

  &__distance {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #94a3b8;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__radio {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

.pharmacy-radio {
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &--checked {
    border-color: #002d5e;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #002d5e;
  }
}

.pharmacy-tag {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;

  &--delivery {
    background: #dbeafe;
    color: #1d4ed8;
  }
  &--online {
    background: #ede9fe;
    color: #6d28d9;
  }
}

// ─── Medication list ───
.medication-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.medication-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.15s;
  position: relative;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &--selected {
    border-color: #05e5ba;
    background: #e8fdf8;
  }

  &--original {
    border-left: 3px solid #05e5ba;
  }

  &--disabled {
    opacity: 0.55;
    cursor: not-allowed;

    &:hover {
      border-color: #e2e8f0;
      background: transparent;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 4px;
  }

  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #002d5e;
  }

  &__presentation {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
    margin-bottom: 8px;
  }

  &__pricing {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 6px;
  }

  &__price {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 16px;
    font-weight: 800;
    color: #002d5e;
  }

  &__coverage,
  &__copay {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
  }

  &__stock {
    margin-top: 4px;
  }

  &__check {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 22px;
    height: 22px;
    border: 2px solid #cbd5e1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #002d5e;
  }

  &--selected &__check {
    border-color: #05e5ba;
    background: #05e5ba;
  }
}

.med-badge {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;

  &--original {
    background: #e8fdf8;
    color: #047857;
  }
  &--generic {
    background: #dbeafe;
    color: #1d4ed8;
  }
  &--similar {
    background: #fef3c7;
    color: #b45309;
  }
}

.stock-badge {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;

  &--ok {
    background: #dcfce7;
    color: #16a34a;
  }
  &--no {
    background: #fee2e2;
    color: #dc2626;
  }
}

// ─── Confirm section ───
.confirm-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 20px;
}

.confirm-block {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    color: #6b7294;
  }

  &__value {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #002d5e;

    &--discount {
      color: #16a34a;
    }

    &--total {
      font-size: 16px;
      font-weight: 800;
    }
  }

  &--total {
    padding-top: 12px;
  }
}

.confirm-delivery {
  ::v-deep .el-radio__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
  }
  ::v-deep .el-radio__input.is-checked .el-radio__inner {
    border-color: #002d5e;
    background: #002d5e;
  }
  ::v-deep .el-radio__input.is-checked + .el-radio__label {
    color: #002d5e;
  }
}

.confirm-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 12px 0;
}

// ─── Step actions ───
.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}

.btn-primary {
  background: #002d5e !important;
  border-color: #002d5e !important;
  color: #fff !important;
  border-radius: 8px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  padding: 10px 24px !important;
  &:hover {
    opacity: 0.9;
  }
  &.is-disabled {
    opacity: 0.4;
  }
}
.btn-ghost {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  color: #6b7294 !important;
  border-radius: 8px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  padding: 10px 24px !important;
  &:hover {
    border-color: #002d5e !important;
    color: #002d5e !important;
  }
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 32px;
  color: #94a3b8;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
}

@media (max-width: 768px) {
  // Card principal
  .purchase-card {
    padding: 16px 14px;
    border-radius: 8px;
  }

  .purchase-header {
    margin-bottom: 16px;

    &__title {
      font-size: 15px;
    }
  }

  // Stepper: números más chicos, labels reducidos, líneas cortas
  .steps {
    margin-bottom: 16px;
    gap: 0;
  }

  .step {
    gap: 4px;

    &__number {
      width: 24px;
      height: 24px;
      font-size: 11px;
    }

    &__label {
      font-size: 10px;
    }

    &__line {
      margin: 0 6px;
      flex: 0 1 16px;
    }
  }

  // Resumen de receta
  .prescription-summary {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 14px;
    gap: 10px;

    &__right {
      flex-wrap: wrap;
      gap: 8px;
    }

    &__drug {
      font-size: 13px;
    }

    &__meta,
    &__doctor,
    &__date {
      font-size: 11px;
    }
  }

  // Farmacia split: vertical, mapa debajo con 250px
  .pharmacy-split {
    flex-direction: column;
    min-height: auto;

    &__list,
    &__map {
      flex: 1 1 auto;
      width: 100%;
    }
  }

  .pharmacy-list-scroll {
    max-height: 280px;
  }

  .pharmacy-map {
    height: 250px;
    min-height: 250px;
  }

  // Cards de farmacia: full width, padding reducido
  .pharmacy-row {
    padding: 10px 12px;
    gap: 10px;

    &__icon {
      width: 34px;
      height: 34px;
      min-width: 34px;

      .micon {
        width: 16px;
        height: 16px;
      }
    }

    &__name {
      font-size: 13px;
    }

    &__address {
      font-size: 11px;
    }

    &__distance {
      display: none;
    }
  }

  // Section title
  .section-title {
    font-size: 13px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  // Cards de medicamento: pricing apilado debajo del nombre
  .medication-card {
    padding: 12px 14px;

    &__name {
      font-size: 13px;
    }

    &__presentation {
      font-size: 11px;
    }

    &__pricing {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    &__price {
      font-size: 14px;
    }

    &__coverage,
    &__copay {
      font-size: 11px;
    }

    &__check {
      top: 12px;
      right: 12px;
    }
  }

  // Confirmación: full width, fonts más chicos
  .confirm-section {
    padding: 14px 16px;
  }

  .confirm-block {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 6px 0;

    &__label {
      font-size: 11px;
    }

    &__value {
      font-size: 12px;

      &--total {
        font-size: 14px;
      }
    }
  }

  .confirm-delivery {
    width: 100%;
  }

  // Botones: apilados verticalmente, full width
  .step-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .btn-primary,
  .btn-ghost {
    width: 100% !important;
    text-align: center;
    padding: 10px 16px !important;
  }
}
</style>

<style lang="scss">
/* Leaflet marker styles (unscoped — Leaflet renders outside Vue scope) */
.pharmacy-marker {
  background: none !important;
  border: none !important;
}

.pharmacy-marker__pin {
  width: 24px;
  height: 24px;
  border-radius: 50% 50% 50% 0;
  background: #002d5e;
  transform: rotate(-45deg);
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  transition: all 0.2s;

  &--selected {
    width: 30px;
    height: 30px;
    background: #05e5ba;
    border-color: #002d5e;
    box-shadow: 0 3px 10px rgba(5, 229, 186, 0.5);
  }
}
</style>
